import React from "react";

function WeGotYou() {
  const services = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: "Digital Product Development",
      description: "Mobile apps, web applications, and user engagement solutions"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
      title: "Branding & Identity Design",
      description: "Crafting unique visual identities that stand out"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: "Open-Source Solutions",
      description: "Scalable, flexible, and cost-effective platforms"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      title: "E-commerce & Online Platforms",
      description: "User-friendly, secure, and visually appealing stores"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: "Digital Marketing & SEO",
      description: "Data-driven campaigns for traffic and conversions"
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      title: "Continuous Integration & Deployment",
      description: "Streamlining development for faster, reliable releases"
    }
  ];

  const features = [
    "Dynamic reports and dashboards",
    "Templates for everyone",
    "Development workflow",
    "Limitless business automation",
    "Knowledge management"
  ];

  return (
    <section id="we-got-you" className="bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="mx-auto max-w-screen-xl space-y-12 px-4 py-16 lg:space-y-20 lg:px-6 lg:py-24">
        {/* First Block */}
        <div className="items-center gap-8 lg:grid lg:grid-cols-2 xl:gap-16">
          <div className="text-gray-500 dark:text-gray-400 sm:text-lg animate-slide-in-left">
            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              Comprehensive IT Services
            </h2>
            <p className="mb-8 text-gray-600 dark:text-gray-300 lg:text-xl leading-relaxed">
              Deliver great service experiences fast - without the complexity of traditional ITSM solutions. Accelerate critical development work, eliminate toil, and deploy changes with ease.
            </p>

            <ul className="my-8 space-y-4 border-t border-gray-200 pt-8 dark:border-gray-700">
              {services.map((service, index) => (
                <li
                  key={index}
                  className="flex space-x-4 p-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300 group"
                >
                  <div className="flex-shrink-0">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 dark:bg-accent-900 text-accent-600 dark:text-accent-300 group-hover:scale-110 transition-transform duration-300">
                      {service.icon}
                    </div>
                  </div>
                  <div>
                    <span className="text-base font-semibold leading-tight text-gray-900 dark:text-white block mb-1">
                      {service.title}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {service.description}
                    </span>
                  </div>
                </li>
              ))}
            </ul>

            <p className="text-gray-600 dark:text-gray-300 lg:text-lg">
              Deliver great service experiences fast - without the complexity of traditional solutions.
            </p>
          </div>

          <img
            className="hidden w-full rounded-lg lg:flex animate-slide-in-right"
            src="/images/feature-1.png"
            alt="IT services dashboard and digital product development"
            loading="lazy"
          />
        </div>

        {/* Second Block */}
        <div className="items-center gap-8 lg:grid lg:grid-cols-2 xl:gap-16">
          <img
            className="hidden w-full rounded-lg lg:flex animate-slide-in-left"
            src="/images/feature-2.png"
            alt="Technology solutions and automation features"
            loading="lazy"
          />

          <div className="text-gray-500 dark:text-gray-400 sm:text-lg animate-slide-in-right">
            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              Investing in Your Success
            </h2>
            <p className="mb-8 text-gray-600 dark:text-gray-300 lg:text-xl leading-relaxed">
              Deliver great service experiences fast - without the complexity of traditional ITSM solutions. Accelerate critical development work, eliminate toil, and deploy changes with ease.
            </p>

            <ul className="my-8 space-y-4 border-t border-gray-200 pt-8 dark:border-gray-700">
              {features.map((feature, index) => (
                <li key={index} className="flex space-x-3 items-center group">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-accent-600 dark:text-accent-400 group-hover:scale-125 transition-transform duration-300"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-base font-medium leading-tight text-gray-900 dark:text-white">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <p className="text-gray-600 dark:text-gray-300 lg:text-lg">
              Reduce operational costs and concentrate on what truly matters for your business growth.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WeGotYou;
